import { AddRounded, CloseRounded, FilterAltRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type {
  BooleanFilterCondition,
  BooleanFilterGroup,
  BooleanFilterOp,
  ContainsFilterKey,
  WidgetFilterGatePermissions,
} from '../lib/dashboardFilters';

const DIMENSION_OPTIONS: { key: ContainsFilterKey; label: string }[] = [
  { key: 'advertiserIds', label: 'Advertiser' },
  { key: 'campaignIds', label: 'Campaign' },
  { key: 'lineItemIds', label: 'Line Item' },
  { key: 'channelIds', label: 'Channel' },
  { key: 'deviceIds', label: 'Device' },
  // Product removed (flank#5059) — see dashboardFilters.ts.
  { key: 'dsps', label: 'DSP' },
  { key: 'creativeIds', label: 'Creative' },
  { key: 'publishers', label: 'Publisher' },
];

/** Same gate as `availableWidgetFilterKeys` (dashboardFilters.ts) — DSP needs
 * "Show DSP / Platform", Publisher needs raw-inventory visibility (flank#5097).
 * Declared here rather than imported since this file's options are keyed by
 * `ContainsFilterKey`/label, not the `WidgetFilterKey` list that lives there. */
const DIMENSION_GATES: Partial<Record<ContainsFilterKey, keyof WidgetFilterGatePermissions>> = {
  dsps: 'canViewPlatform',
  publishers: 'canViewRawInventory',
};

const OP_LABELS: Record<BooleanFilterOp, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
};

const emptyCondition = (): BooleanFilterCondition => ({
  key: 'advertiserIds',
  op: 'contains',
  values: [],
  id: crypto.randomUUID(),
});
const emptyGroup = (): BooleanFilterGroup => ({ op: 'OR', conditions: [emptyCondition()], id: crypto.randomUUID() });

/** A condition with no values yet doesn't count as "active" — it's still being filled in. */
const activeConditionCount = (groups: BooleanFilterGroup[]): number =>
  groups.reduce((n, g) => n + g.conditions.filter(c => c.values.length > 0).length, 0);

/**
 * A condition's comma-separated value list, edited as free text. Kept as its
 * own local buffer rather than a value derived straight from `values` on
 * every keystroke — that binding split on ',' and trimmed on each change,
 * which erased a just-typed trailing space or comma before the next
 * character could land, making a multi-word value ("Moving Team") or a
 * second comma-separated term impossible to type (STORM review, flank#4515).
 * Parses and commits to the parent on blur or Enter instead.
 */
const ConditionValuesInput = ({
  values,
  onCommit,
}: {
  values: string[];
  onCommit: (values: string[]) => void;
}) => {
  const [text, setText] = useState(values.join(', '));

  // A saved-view selection or reset can replace `values` on a condition that
  // keeps the same `id` (so this input stays mounted) — without this, the
  // buffer holds stale text from the previous condition and can write it back
  // on the next blur/Enter (CodeRabbit, flank#4515).
  useEffect(() => {
    setText(values.join(', '));
  }, [values]);

  const commit = () => {
    const parsed = text
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
    onCommit(parsed);
    setText(parsed.join(', '));
  };

  return (
    <TextField
      size='small'
      placeholder='value, value'
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        // CJK input confirms a composition candidate with Enter — committing
        // partial text on that keystroke breaks the composition (CodeRabbit,
        // flank#4515).
        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          commit();
        }
      }}
      sx={{ flex: 1 }}
    />
  );
};

/**
 * Boolean AND/OR/NOT filter builder (FB-007) — the one composition the rest of
 * the filter bar can't express, since every other filter is AND'd together.
 * Each group is one flat ALL(AND)/ANY(OR) match over its own conditions, which
 * may span different dimensions (e.g. Channel contains "CTV" OR Device is
 * "Mobile"); multiple groups AND with each other and with the rest of the bar.
 * No nesting — see dashboardFilters.ts for why that's enough.
 */
const AdvancedFilterBuilder = ({
  groups,
  onChange,
  permissions,
}: {
  groups: BooleanFilterGroup[];
  onChange: (next: BooleanFilterGroup[]) => void;
  permissions: WidgetFilterGatePermissions;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const activeCount = activeConditionCount(groups);
  // DSP/Publisher dropped for a viewer without the matching permission
  // (flank#5097) — the server would silently drop a condition on either
  // anyway, so offering them here just invites a condition that never filters.
  const dimensionOptions = useMemo(
    () =>
      DIMENSION_OPTIONS.filter(d => {
        const gate = DIMENSION_GATES[d.key];
        return !gate || permissions[gate];
      }),
    [permissions],
  );

  const updateGroup = (i: number, next: BooleanFilterGroup) => onChange(groups.map((g, idx) => (idx === i ? next : g)));
  const removeGroup = (i: number) => onChange(groups.filter((_, idx) => idx !== i));
  const addGroup = () => onChange([...groups, emptyGroup()]);

  const updateCondition = (gi: number, ci: number, next: BooleanFilterCondition) => {
    const group = groups[gi];
    updateGroup(gi, { ...group, conditions: group.conditions.map((c, idx) => (idx === ci ? next : c)) });
  };
  const removeCondition = (gi: number, ci: number) => {
    const group = groups[gi];
    const conditions = group.conditions.filter((_, idx) => idx !== ci);
    if (conditions.length === 0) removeGroup(gi);
    else updateGroup(gi, { ...group, conditions });
  };
  const addCondition = (gi: number) => {
    const group = groups[gi];
    updateGroup(gi, { ...group, conditions: [...group.conditions, emptyCondition()] });
  };

  return (
    <>
      <Button
        size='small'
        startIcon={<FilterAltRounded sx={{ fontSize: 16 }} />}
        onClick={e => setAnchorEl(e.currentTarget)}
        sx={{ textTransform: 'none', color: activeCount > 0 ? 'primary.main' : 'text.secondary' }}
      >
        Advanced filter
        {activeCount > 0 && <Chip label={activeCount} size='small' sx={{ ml: 0.75, height: 18, fontSize: 11 }} />}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 480, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='subtitle2'>Advanced filter</Typography>
          {groups.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              Combine conditions across dimensions with AND/OR/NOT — e.g. Channel contains "CTV" OR Device is "Mobile".
            </Typography>
          )}
          {groups.map((group, gi) => (
            <Box
              key={group.id ?? gi}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='caption' color='text.secondary'>
                  Match
                </Typography>
                <ToggleButtonGroup
                  size='small'
                  exclusive
                  value={group.op}
                  onChange={(_, value) => value && updateGroup(gi, { ...group, op: value })}
                >
                  <ToggleButton value='AND' sx={{ px: 1, fontSize: 11 }}>
                    ALL
                  </ToggleButton>
                  <ToggleButton value='OR' sx={{ px: 1, fontSize: 11 }}>
                    ANY
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant='caption' color='text.secondary'>
                  of these conditions:
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton size='small' onClick={() => removeGroup(gi)} aria-label='Remove condition group'>
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              {group.conditions.map((condition, ci) => (
                <Box key={condition.id ?? ci} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Select
                    size='small'
                    value={condition.key}
                    onChange={e => updateCondition(gi, ci, { ...condition, key: e.target.value as ContainsFilterKey })}
                    sx={{ minWidth: 130 }}
                  >
                    {dimensionOptions.map(d => (
                      <MenuItem key={d.key} value={d.key}>
                        {d.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    size='small'
                    value={condition.op}
                    onChange={e => updateCondition(gi, ci, { ...condition, op: e.target.value as BooleanFilterOp })}
                    sx={{ minWidth: 140 }}
                  >
                    {Object.entries(OP_LABELS).map(([op, label]) => (
                      <MenuItem key={op} value={op}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <ConditionValuesInput
                    values={condition.values}
                    onCommit={values => updateCondition(gi, ci, { ...condition, values })}
                  />
                  <IconButton size='small' onClick={() => removeCondition(gi, ci)} aria-label='Remove condition'>
                    <CloseRounded sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size='small'
                startIcon={<AddRounded sx={{ fontSize: 14 }} />}
                onClick={() => addCondition(gi)}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                Add condition
              </Button>
            </Box>
          ))}
          <Button
            size='small'
            startIcon={<AddRounded sx={{ fontSize: 14 }} />}
            onClick={addGroup}
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            Add condition group
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AdvancedFilterBuilder;
